import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../store';
import { apiVariables } from '../../services/api/apiVariables';
import { getToken } from '../../utils/tokenUtils';
import { Admin_GetUserDetail_LIST } from '../../constants/adminData';
import { updatePersonalInfo } from '../../redux/slices/userProfileSlice';
import { getSensitiveData } from '../../utils/secureStorage';
import { PhotoImage } from './PhotoImage';

interface CommentItem {
    id: number;
    userId: string;
    commentText: string;
    createdAt: string;
    userName?: string;
    userImage?: string;
    profilePhotoId?: string | number;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    photoId: string;
    readOnly?: boolean;
}

export default function CommentsModal({ isOpen, onClose, photoId, readOnly }: Props) {
    const [comments, setComments] = useState<CommentItem[]>([]);
    const [commentText, setCommentText] = useState('');
    const [loading, setLoading] = useState(false);
    const [posting, setPosting] = useState(false);
    const profile = useAppSelector((state) => state.userProfile);
    const dispatch = useDispatch();

    const [currentUserImage, setCurrentUserImage] = useState('');
    const [loadedUserName, setLoadedUserName] = useState('');
    const listRef = useRef<HTMLDivElement | null>(null);

    const currentUserName = useMemo(() => {
        const firstName = profile?.personalInfo?.firstName || '';
        const lastName = profile?.personalInfo?.lastName || '';
        const name = `${firstName} ${lastName}`.trim();
        return name || loadedUserName;
    }, [profile?.personalInfo?.firstName, profile?.personalInfo?.lastName, loadedUserName]);

    useEffect(() => {
        if (!isOpen) return;
        if (profile?.personalInfo?.firstName) return;

        const loadUserInfo = async () => {
            const userId = profile?.userId || (await getSensitiveData('userId'));
            if (!userId) return;

            try {
                const resp = await Admin_GetUserDetail_LIST(userId, (await getToken()) || '');
                if (resp?.success && resp.data) {
                    const d: any = resp.data;
                    const firstName = d.firstName || '';
                    const lastName = d.lastName || '';
                    const name = `${firstName} ${lastName}`.trim();
                    if (name) {
                        setLoadedUserName(name);
                        dispatch(updatePersonalInfo({ firstName, lastName }));
                    }
                }
            } catch (err) {
                console.error('Error loading user info:', err);
            }
        };

        loadUserInfo();
    }, [isOpen, profile?.personalInfo?.firstName, profile?.userId, dispatch]);

    function getInitials(name: string) {
        if (!name) return 'U';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    }

    useEffect(() => {
        if (!isOpen) return;
        if (!photoId) return;

        let mounted = true;
        const controller = new AbortController();

        const load = async (): Promise<CommentItem[]> => {
            if (!mounted) return [];
            setLoading(true);
            try {
                const url = `${apiVariables.PhotoLike}/${photoId}/comments`;
                const res = await fetch(url, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${await getToken()}`,
                    },
                    signal: controller.signal,
                });

                if (!res.ok) {
                    console.error('Failed to load comments', res.statusText);
                    if (mounted) setComments([]);
                    return [];
                }

                const json = await res.json();
                const arr = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];

                const mapped: CommentItem[] = arr.map((c: any) => ({
                    id: c.photoCommentId ?? c.id ?? Date.now(),
                    userId: c.commentedByUserId || c.userId || '',
                    commentText: c.commentText || '',
                    createdAt: c.createdAt || c.updatedAt || new Date().toISOString(),
                    userName: c.user ? `${c.user.firstName || ''} ${c.user.lastName || ''}`.trim() || c.userName || c.commentedByUserName : (c.userName || c.commentedByUserName || undefined),
                    userImage: c.user?.profileImageUrl || c.profileImageUrl || undefined,
                    profilePhotoId: c.user?.profilePhotoId || undefined,
                }));

                if (mounted) setComments(mapped);
                return mapped;
            } catch (err) {
                if (mounted) {
                    console.error('Error fetching comments', err);
                    setComments([]);
                }
                return [];
            } finally {
                if (mounted) setLoading(false);
            }
        };

        const loadCurrentUser = async (loadedComments: CommentItem[] = []) => {
            try {
                const localUserId = (profile?.userId || (await getSensitiveData('userId')) || '') as string;

                if (profile?.personalInfo?.firstName) {
                    const reduxImage = profile.personalInfo.profileImage || (profile.personalInfo as any).profileImageUrl || '';
                    if (reduxImage && mounted) {
                        setCurrentUserImage(reduxImage);
                        return;
                    }
                }

                if (!localUserId) return;

                const ownFromComments = loadedComments.find((c) => String(c.userId) === String(localUserId));
                if (ownFromComments && ownFromComments.userImage) {
                    const name = ownFromComments.userName || '';
                    if (mounted) {
                        setCurrentUserImage(ownFromComments.userImage || '');
                        try {
                            const parts = (name || '').trim().split(/\s+/);
                            const firstName = parts[0] || '';
                            const lastName = parts.slice(1).join(' ') || '';
                            dispatch(updatePersonalInfo({ firstName, lastName, profileImage: ownFromComments.userImage }));
                        } catch { }
                    }
                    return;
                }

                const resp = await Admin_GetUserDetail_LIST(localUserId, (await getToken()) || '');
                if (resp?.success && resp.data && mounted) {
                    const d: any = resp.data;
                    const firstName = d.firstName || d.personalInfo?.firstName || '';
                    const lastName = d.lastName || d.personalInfo?.lastName || '';
                    const profileImage = d.profileImageUrl || d.personalInfo?.profileImage || '';
                    setCurrentUserImage(profileImage || '');
                    try { dispatch(updatePersonalInfo({ firstName, lastName, profileImage })); } catch { }
                }
            } catch (err) {
                if (mounted) console.error('Error loading current user profile', err);
            }
        };

        (async () => {
            const mappedComments = await load();
            await loadCurrentUser(mappedComments);
        })();

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [isOpen, photoId]);

    useEffect(() => {
        if (!isOpen) return;
        const t = setTimeout(() => {
            try {
                if (listRef.current) {
                    listRef.current.scrollTop = listRef.current.scrollHeight;
                }
            } catch (e) {
            }
        }, 150);
        return () => clearTimeout(t);
    }, [isOpen, comments.length, loading]);

    const handlePostComment = async () => {
        if (!photoId || !commentText.trim()) return;
        setPosting(true);
        try {
            const url = `${apiVariables.PhotoLike}/${photoId}/comments`;
            const body = {
                userPhotoId: Number(photoId),
                commentText: commentText.trim(),
            };

            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${await getToken()}`,
                },
                body: JSON.stringify(body),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => null);
                console.error('Failed to post comment:', err?.message || res.statusText);
                return;
            }

            const created = await res.json();
            const serverObj = created?.data ?? created;

            if (!serverObj) return;

            const newComment: CommentItem = {
                id: serverObj.photoCommentId ?? serverObj.id ?? Date.now(),
                userId: serverObj.commentedByUserId || serverObj.userId || ((await getSensitiveData('userId')) || ''),
                commentText: serverObj.commentText || commentText.trim(),
                createdAt: serverObj.createdAt || serverObj.updatedAt || new Date().toISOString(),
                userName: serverObj.userName || currentUserName || undefined,
                userImage: serverObj.userImage || serverObj.profileImageUrl || currentUserImage || undefined,
                profilePhotoId: serverObj.user?.profilePhotoId || undefined,
            };


            setComments((prev) => [...prev, newComment]);
            setCommentText('');

            try {
                const serverUser = serverObj?.user;
                if (serverUser) {
                    const firstName = serverUser.firstName || serverUser.personalInfo?.firstName || '';
                    const lastName = serverUser.lastName || serverUser.personalInfo?.lastName || '';
                    const image = serverUser.profileImageUrl || serverUser.personalInfo?.profileImage || '';

                    setComments((prev) => prev.map((c) => c.id === newComment.id ? { ...c, userName: c.userName || `${firstName} ${lastName}`.trim(), userImage: c.userImage || image } : c));
                }
            } catch (e) {
            }

        } catch (err) {
            console.error('Error posting comment', err);
        } finally {
            setPosting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-40 flex items-end justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            <div
                className="relative w-full bg-white rounded-t-lg shadow-xl p-4 md:p-6 max-h-[90vh] flex flex-col overflow-hidden"
                style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
            >
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">Comments</h3>
                    <button onClick={onClose} className="text-gray-600">Close</button>
                </div>

                <div ref={listRef} className="overflow-y-auto max-h-[calc(90vh-8rem)] space-y-3 pb-4">
                    {loading ? (
                        <div className="text-center py-6">Loading Comments...</div>
                    ) : comments.length === 0 ? (
                        <div className="text-center text-sm text-gray-500 py-6">No comments yet. Be the first to comment.</div>
                    ) : (
                        comments.map((c) => (
                            <div key={c.id} className="flex gap-3 md:gap-4 items-start">
                                <PhotoImage
                                    photoId={c.profilePhotoId}
                                    alt={c.userName || 'User'}
                                    className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full object-cover"
                                    fallback={
                                        <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full bg-gray-200 flex items-center justify-center text-xs md:text-sm lg:text-base font-medium">
                                            {getInitials(c.userName || c.userId || '')}
                                        </div>
                                    }
                                />

                                <div className="flex-1 min-w-0">
                                    <div className="text-sm md:text-base lg:text-base font-semibold truncate">{c.userName || 'User'}</div>
                                    <div className="text-sm md:text-base text-gray-700 break-words">{c.commentText}</div>
                                    <div className="text-xs md:text-sm text-gray-400 mt-1">{getDateTimeLabel(c.createdAt)}</div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="pt-3 border-t border-gray-100 flex gap-1 items-center">
                    {!readOnly ? (
                        <>
                            <div className="flex items-center gap-2">
                                <PhotoImage
                                    photoId={profile.personalInfo?.photoId}
                                    alt={currentUserName || 'You'}
                                    className="w-10 h-10 rounded-full object-cover"
                                    fallback={
                                        <div id="current-user-avatar" className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                                            {getInitials(currentUserName)}
                                        </div>
                                    }
                                />
                            </div>
                            <input
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder={currentUserName ? `Comment as ${currentUserName}` : 'Write a comment...'}
                                className="flex-1 min-w-0 rounded-full px-2 py-2 border border-gray-300 focus:outline-none focus:border-brand-primary"
                                disabled={posting}
                            />
                            <button
                                onClick={handlePostComment}
                                className="bg-transparent text-brand-primary p-2 rounded-full border border-solid border-brand-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={posting || !commentText.trim()}
                                aria-label="Send Comment"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="w-6 h-6"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 2.25l9 19.5-9-7.5-9 7.5 9-19.5z"
                                    />
                                </svg>
                            </button>
                        </>
                    ) : (
                        <div className="flex-1" />
                    )}
                </div>
            </div>
        </div>
    );
}
function getDateLabel(date: Date): string {
    const today = new Date();
    const yesterday = new Date();

    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (d1: Date, d2: Date) =>
        d1.getDate() === d2.getDate() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getFullYear() === d2.getFullYear();

    if (isSameDay(date, today)) return "Today";
    if (isSameDay(date, yesterday)) return "Yesterday";

    return date.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

function getDateTimeLabel(dateString: string): string {
    const localDate = parseApiDate(dateString); // ✅ fix here

    const dateLabel = getDateLabel(localDate);

    const time = localDate.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });

    return `${dateLabel}, ${time}`;
}
function parseApiDate(dateString: string): Date {
    if (!dateString) return new Date();

    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(dateString);
    const safe = hasTimezone ? dateString : `${dateString}Z`;

    return new Date(safe);
}